import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { FilterGroup } from '../../components/filters/filter-panel.type';
import { useTeams } from '../../components/global-filter/global-filter.hooks';
import { GlobalFilterContext } from '../../contexts/global-filter';
import { useDeepCompareEffect } from '../../hooks/useDeepCompareEffect';

/**
 * Hook for managing filters in the Agentic Process view.
 * Provides functionality for team and board selection, filter reset, and filter group configuration.
 *
 * @returns {Object} An object containing:
 * - team: The currently selected team
 * - board: The currently selected board
 * - filterGroups: Array of filter groups for the filter panel
 * - handleResetFilters: Function to reset filters to default values
 */
const useAgenticProcessFilters = () => {
  const i18n = useTranslation();
  const { team, setTeam, board, setBoard } = useContext(GlobalFilterContext);
  const { teams, boards } = useTeams();
  const hasInitializedRef = useRef(false);

  // Ensure board is null on initial render - user must actively select
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      if (board !== null) {
        setBoard(null);
      }
    }
  }, [board, setBoard]);

  // Handle team selection
  const handleTeamSelection = useCallback(
    (values: string[]) => {
      if (values.length > 0) {
        const selectedTeam = teams.find((t) => t.id === values[0]);
        if (selectedTeam) {
          setTeam(selectedTeam);
          // Reset board to null when team changes - user must actively select
          setBoard(null);
        }
      } else if (teams.length > 0) {
        // Don't allow empty selection - fallback to current or first team
        const teamToSelect = team || teams[0];
        setTeam(teamToSelect);
        // Set board to null when resetting team
        setBoard(null);
      }
    },
    [teams, team, setTeam, setBoard],
  );

  // Handle board selection
  const handleBoardSelection = useCallback(
    (values: string[]) => {
      if (values.length > 0) {
        const id = values[0];
        const selectedBoard = boards.find((b) => b.id === id);
        if (selectedBoard) {
          setBoard(selectedBoard);
        } else {
          // ID no longer valid (e.g., dataset refreshed); clear selection
          setBoard(null);
        }
      } else {
        setBoard(null);
      }
    },
    [boards, setBoard],
  );

  // Reset all filters
  const handleResetFilters = useCallback(() => {
    if (teams.length > 0) {
      const firstTeam = teams[0];
      setTeam(firstTeam);
    }
    // Always clear the board
    setBoard(null);
  }, [teams, setTeam, setBoard]);

  // Create filter groups
  const filterGroups: FilterGroup[] = useMemo(() => {
    const groups: FilterGroup[] = [];

    // Team filter group
    const teamFilterGroup: FilterGroup = {
      name: i18n.t('common.project'), // "Team"
      options: teams.map((t) => ({ value: t.id, label: t.name })),
      selectedValues: team ? [team.id] : [],
      onSelectionChange: handleTeamSelection,
      optional: false,
    };
    groups.push(teamFilterGroup);

    // Board filter group
    const boardFilterGroup: FilterGroup = {
      name: i18n.t('common.board'), // "Board"
      icon: 'dashboard',
      options: boards.map((b) => ({ value: b.id, label: b.name })),
      selectedValues: board ? [board.id] : [],
      onSelectionChange: handleBoardSelection,
      optional: true, // Allow null/empty selection
    };
    groups.push(boardFilterGroup);

    return groups;
  }, [teams, boards, team, board, handleTeamSelection, handleBoardSelection, i18n.t]);

  // Reset when global teams array changes (portfolio switch)
  useDeepCompareEffect(() => {
    const defaultTeam = teams.length > 0 ? teams[0] : null;
    setTeam(defaultTeam);
    setBoard(null);
  }, [teams]);

  return {
    team,
    board,
    filterGroups,
    handleResetFilters,
  };
};

export { useAgenticProcessFilters };
